import axiosClient from './axiosClient';
import API from './endpoints';

/**
 * WS-E.4 — fetch a single attachment's bytes for in-console rendering
 * (thumbnail preview / chip download). Goes through the authed axiosClient
 * so Authorization + X-Tenant-Id + X-Transaction-Id are attached the same
 * way as every other request. May reject — callers (MessageAttachments)
 * degrade to a plain chip on failure.
 *
 * WS-E.R-B2 — lives in its own module rather than endpoints.ts. endpoints.ts
 * is a pure constants leaf with zero imports; axiosClient.ts itself imports
 * `API` from endpoints.ts (for API.AUTH.REFRESH). Hosting this function in
 * endpoints.ts would make endpoints.ts import axiosClient.ts, which imports
 * endpoints.ts — a genuine import cycle that only "worked" because
 * axiosClient.ts only dereferences API.AUTH.REFRESH inside a deferred
 * function body.
 */
export async function fetchAttachmentBlob(
  fileId: string,
  plane: 'conversation' | 'knowledge' = 'conversation',
): Promise<Blob> {
  // 2026-09-05 - two planes, two owning services, two routes (mirrors the widget's endpoints.ts).
  // Before this the console always hit /api/media and every bank image the bot sent rendered as a
  // broken chip for staff.
  const url = plane === 'knowledge' ? API.MEDIA.KNOWLEDGE_CONTENT(fileId) : API.MEDIA.CONTENT(fileId);
  const response = await axiosClient.get(url, {
    responseType: 'blob',
  });
  return response.data as Blob;
}

/** Server-advertised upload policy. Mirrors the widget's MediaConfig. */
export interface MediaConfig {
  enabled: boolean;
  maxFileSizeBytes: number;
  allowedMimeTypes: string[];
}

/**
 * Fail-closed default. Used whenever GET /api/media/config is unreachable or has not resolved
 * yet — with the feature dark the console must look EXACTLY as it does today, so "unknown"
 * resolves to "off", never to "allow everything".
 */
export const DISABLED_MEDIA_CONFIG: MediaConfig = {
  enabled: false,
  maxFileSizeBytes: 0,
  allowedMimeTypes: [],
};

/**
 * Fetch the upload policy. MUST NEVER throw: this runs during workspace boot, and a failure
 * here must not take down the thread view. Every field is coerced defensively so a malformed
 * response cannot leak through as a truthy config.
 */
export async function getMediaConfig(): Promise<MediaConfig> {
  try {
    const { data } = await axiosClient.get(API.MEDIA.CONFIG);
    return {
      enabled: !!data?.enabled,
      maxFileSizeBytes: typeof data?.maxFileSizeBytes === 'number' ? data.maxFileSizeBytes : 0,
      allowedMimeTypes: Array.isArray(data?.allowedMimeTypes) ? data.allowedMimeTypes : [],
    };
  } catch {
    return DISABLED_MEDIA_CONFIG;
  }
}

/**
 * Response shape of a successful upload — the server's MediaFileResponse, camelCase on the wire.
 * `id` is the REAL identifier; `fileId` is a forward-compat fallback only and is NOT emitted
 * today. Reading `fileId` alone is what once produced attachmentIds:[null] on every upload in
 * the customer widget, so this type documents the trap rather than repeating it.
 */
export interface MediaUploadResult {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  fileId?: string;
}

/** Upload one file against a session. Rejects on failure — callers surface the reason. */
export async function uploadFile(sessionId: string, file: File): Promise<MediaUploadResult> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axiosClient.post(API.MEDIA.UPLOAD(sessionId), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data as MediaUploadResult;
}
